/**
 * Product Carousel
 * Initializes Splide carousels with viewport-gated recommendations loading.
 */

import { hydrateDeferredSecondaryImages } from './collection-card-secondary-images.js';

(function() {
  const initializedSections = new Set();

  /**
   * Parse carousel config from section data attribute.
   * @param {HTMLElement} sectionContainer
   * @returns {object|null}
   */
  function getCarouselConfig(sectionContainer) {
    const configNode = sectionContainer.querySelector('[data-product-carousel-config]');
    if (!configNode || !configNode.textContent) {
      return null;
    }

    try {
      return JSON.parse(configNode.textContent);
    } catch (error) {
      console.warn('product-carousel: invalid config JSON', error);
      return null;
    }
  }

  /**
   * Wait for Splide to be available.
   * @returns {Promise<void>}
   */
  async function waitForSplide() {
    if (typeof window.__splideReady !== 'undefined') {
      try {
        await window.__splideReady;
      } catch (error) {
        console.warn('product-carousel: Splide library failed to load.', error);
      }
      return;
    }

    if (window.Splide) {
      return;
    }

    await new Promise(function(resolve) {
      let attempts = 0;
      const maxAttempts = 50;

      const checkSplide = setInterval(function() {
        attempts++;

        if (window.Splide) {
          clearInterval(checkSplide);
          resolve();
        } else if (attempts >= maxAttempts) {
          clearInterval(checkSplide);
          console.warn('product-carousel: Splide library failed to load within timeout.');
          resolve();
        }
      }, 100);
    });
  }

  /**
   * Mount Splide on a carousel element.
   * @param {HTMLElement} sectionContainer
   * @param {object} config
   */
  function initSplide(sectionContainer, config) {
    if (typeof window.Splide === 'undefined') {
      return;
    }

    const splideElement = sectionContainer.querySelector('#' + config.splideId);
    if (!splideElement) {
      return;
    }

    const splideConfig = Object.assign({}, config.splideConfig, {
      ariaLabel: config.ariaLabel || 'Carousel',
    });

    const splide = new window.Splide('#' + config.splideId, splideConfig);
    splide.mount();

    hydrateDeferredSecondaryImages(sectionContainer);

    const progressBar = splide.root.querySelector('.sp-progress__bar');
    if (progressBar) {
      /** @type {number | null} */
      let progressFrame = null;
      splide.on('mounted move', function() {
        if (progressFrame !== null) return;
        progressFrame = requestAnimationFrame(function() {
          progressFrame = null;
          const end = splide.Components.Controller.getEnd() + 1;
          const rate = Math.min((splide.index + 1) / end, 1);
          progressBar.style.width = String(100 * rate) + '%';
        });
      });
    }
  }

  /**
   * Mount skeleton Splide while recommendations load.
   * @param {HTMLElement} sectionContainer
   * @param {object} config
   */
  function initSkeletonSplide(sectionContainer, config) {
    if (typeof window.Splide === 'undefined') {
      return;
    }

    const skeletonId = 'splide-skeleton-' + config.sectionId;
    const skeletonElement = sectionContainer.querySelector('#' + skeletonId);
    if (!skeletonElement) {
      return;
    }

    const skeletonConfig = Object.assign({}, config.splideConfig, {
      autoplay: false,
    });

    const skeleton = new window.Splide('#' + skeletonId, skeletonConfig);
    skeleton.mount();
  }

  /**
   * Fetch Shopify recommendations and replace skeleton carousel.
   * @param {HTMLElement} sectionContainer
   * @param {object} config
   */
  async function loadRecommendationsAndInitSplide(sectionContainer, config) {
    const skeletonElement = sectionContainer.querySelector('.product-carousel-skeleton');

    try {
      const url = config.recommendationsUrl +
        '&product_id=' + config.productId +
        '&section_id=' + config.sectionId +
        '&intent=' + config.intent;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error('Failed to load recommendations: ' + response.status);
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const recommendationsSection = doc.querySelector(
        'section[id*="' + config.sectionId + '"]'
      );

      if (recommendationsSection) {
        const splideElement = recommendationsSection.querySelector('.splide');

        if (splideElement && skeletonElement) {
          skeletonElement.insertAdjacentHTML('beforebegin', splideElement.outerHTML);
          skeletonElement.remove();
          initSplide(sectionContainer, config);
        }
      }
    } catch (error) {
    }
  }

  /**
   * Initialize a product carousel section.
   * @param {HTMLElement} sectionContainer
   */
  async function initProductCarousel(sectionContainer) {
    const config = getCarouselConfig(sectionContainer);
    if (!config || !config.sectionId) {
      return;
    }

    if (initializedSections.has(config.sectionId)) {
      return;
    }
    initializedSections.add(config.sectionId);

    await waitForSplide();

    if (config.enableRecommendations) {
      initSkeletonSplide(sectionContainer, config);
      await loadRecommendationsAndInitSplide(sectionContainer, config);
      return;
    }

    initSplide(sectionContainer, config);
  }

  /**
   * Observe carousel sections and init when near viewport.
   */
  function observeCarousels() {
    const sections = document.querySelectorAll('[data-product-carousel]');

    sections.forEach(function(sectionContainer) {
      const config = getCarouselConfig(sectionContainer);
      if (!config || initializedSections.has(config.sectionId)) {
        return;
      }

      if (!('IntersectionObserver' in window)) {
        initProductCarousel(sectionContainer);
        return;
      }

      const observer = new IntersectionObserver(
        function(entries, obs) {
          if (!entries[0] || !entries[0].isIntersecting) {
            return;
          }

          obs.disconnect();
          initProductCarousel(sectionContainer);
        },
        /* Mount before native lazy-images enter the viewport to avoid width blowout */
        { rootMargin: '800px 0px' }
      );

      observer.observe(sectionContainer);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeCarousels);
  } else {
    observeCarousels();
  }

  document.addEventListener('shopify:section:load', function(event) {
    const section = event.target;
    if (!section || !section.querySelector) {
      return;
    }

    const carousel = section.querySelector('[data-product-carousel]');
    if (carousel) {
      initProductCarousel(carousel);
    }
  });
})();
